import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader } from 'lucide-react';

import { Button } from 'components/ui/button';
import {
  getVoiceScanReportDetail,
  processVoiceRecording,
} from 'services/voice-scan/api';
import { useVoiceScanStore } from 'store/voice-scan';
import ProcessingError from './ProcessingError';
import { isVoiceScanReport, isVoiceScanReportDetailError } from 'lib/voice';
import { captureUserActivity } from 'services/profile/api';
import { PATH_ROUTES } from 'routes/path';
import { USER_ACTIVITY } from 'types';

import ReportLoader from 'assets/report_loader.png';
import { useTranslation } from 'react-i18next';

export default function AudioReportGenerating({
  onReset,
}: {
  onReset: () => void;
}) {
  const { t } = useTranslation();
  const sessionId = useVoiceScanStore((state) => state.sessionId);
  const navigate = useNavigate();

  const { isSuccess: isProcessSuccess, error: processError } = useQuery({
    queryKey: ['process-voice-recording', sessionId],
    queryFn: () => processVoiceRecording({ sessionId }),
    enabled: !!sessionId,
  });

  const { data: voiceScanData, error: detailError } = useQuery({
    queryKey: ['voice-scan-report-detail', sessionId],
    queryFn: () => getVoiceScanReportDetail({ sessionId }),
    enabled: isProcessSuccess,
    gcTime: 0,
    staleTime: 0,
    refetchInterval: ({ state }) => {
      if (!state?.data) {
        return 5000;
      }
      if (
        isVoiceScanReport(state?.data) ||
        isVoiceScanReportDetailError(state?.data)
      ) {
        return false;
      }
      return 5000;
    },
    retry: false,
  });

  useEffect(() => {
    if (isVoiceScanReport(voiceScanData)) {
      captureUserActivity(USER_ACTIVITY.VOICE_SCAN_REPORT_GENERATION_COMPLETE, {
        session_id: sessionId,
      });
      navigate(`/voice-scans/${sessionId}`);
    }
  }, [voiceScanData]);

  const isError =
    isVoiceScanReportDetailError(voiceScanData) || processError || detailError;

  if (isError) {
    return <ProcessingError onReset={onReset} />;
  }

  return (
    <div className='grid items-center justify-center h-[calc(100vh-12rem)] lg:h-[calc(100vh-8rem)] max-w-2xl mx-auto pt-10'>
      <div>
        <h2 className='text-xl font-semibold text-center lg:text-2xl'>
          {t('voice_scan_generating_title')}
        </h2>

        <div className='relative mx-auto size-60'>
          <img
            src={ReportLoader}
            alt='loader'
            className='object-cover w-full h-full'
          />
          <div className='absolute z-50 left-[38%] top-[36%]'>
            <Loader className='text-white size-6 animate-spin' />
          </div>
        </div>

        <div className='space-y-6'>
          <p className='text-center text-[#4B5363]'>
            {t('voice_scan_generating_info')}
          </p>
          <div className='flex justify-center'>
            <Button
              onClick={() => navigate(PATH_ROUTES.VOICE_SCAN)}
              className='text-sm lg:text-base h-9 lg:h-10'
            >
              {t('back_to_reports')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
